import { API_URL } from 'constant';
import {
	BusinessRegistrationState,
	IBusinessRegistrationState,
} from 'global-stores';
import { useNetwork } from 'hooks';
import { useState } from 'react';

import { useSetRecoilState } from 'recoil';
import { SignUpSessionStatusState } from 'views/new-user-popup';
import { ISessionDetails } from 'views/new-user-popup/stores/types';

/**
 * ONE COMPLIANCE FETCH IN FLIGHT AT A TIME.
 *
 * `initUserData` in routes.tsx is a useCallback over `userData` inside an effect
 * that ALSO depends on `userData`, so a single dashboard load fired this request
 * twice, 80ms apart — two identical calls, two identical 400s, two console
 * errors, for one answer.
 *
 * Deliberately an in-flight cache and not a "fetched once" flag: the billing and
 * subscription views call this again ON PURPOSE to pick up a compliance record
 * that has just been created, and a once-only guard would silently break the
 * refresh they depend on. Sharing the promise collapses the duplicate without
 * touching the legitimate second call, which arrives long after the first has
 * settled.
 *
 * Module scope, not a ref: the two callers are different component instances, so
 * a per-hook ref would not see each other's request.
 */
let complianceInFlight: Promise<any> | null = null;

export const useCompliance = () => {
	const setSessionDetails = useSetRecoilState(SignUpSessionStatusState);
	const setBusinessRegistration = useSetRecoilState(BusinessRegistrationState);
	const [isSessionLoading, setIsSessionLoading] = useState(false);

	// hook
	const { get } = useNetwork({
		updateState: false,
		returnResponse: true,
	});
	const { get: getSessionData } = useNetwork({ updateState: false });

	const updateBusinessRegistrationData = (result: ISessionDetails) => {
		const {
			sessionDetails = [],
			approveStatus,
			accountType,
			physicalVerificationStatus,
		} = result ?? {};
		// __simplici_session_lookup_fallback__: business.accountType is null until
		// IdentityRegistration lands at the very end of the journey, while the
		// compliance record's own `type` is already 'entity' — so this find missed
		// and wiped kyb/kyc/kybForm out of BusinessRegistrationState. That state is
		// what the identity flow's mount effect reads to decide where to resume, so
		// a fully verified entity was sent back to "Choose account type" / KYC
		// instead of forward. clients.api only ever returns one session detail.
		const sessionResult =
			sessionDetails.find(el => el.type === accountType) ?? sessionDetails[0];
		const { sessionData } = sessionResult ?? {};
		const { kyb = [], kyc = [], kybForm = [], aml = [] } = sessionData ?? {};

		const kybData = structuredClone(kyb?.[0]?.data?.[0]);
		const kycData = structuredClone(kyc?.[0]?.data);
		const kybFormData = structuredClone(kybForm?.[0]);
		const amlData = structuredClone(aml?.[0]?.data);

		if (kycData) {
			kycData['success'] = true;
		}
		if (kybData) {
			kybData['success'] = true;
		}

		setBusinessRegistration((prev: IBusinessRegistrationState) => ({
			...prev,
			approveStatus,
			accountType,
			kyc: kycData,
			kyb: kybData,
			physicalVerificationStatus,
			aml: amlData,
			kybForm: kybFormData,
		}));
	};

	const getComplianceData = async (accessToken?: string) => {
		let configHeader = undefined;
		if (accessToken)
			configHeader = {
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			};
		if (!complianceInFlight) {
			complianceInFlight = get(API_URL.BUSINESS_COMPLIANCE, configHeader).finally(() => {
				complianceInFlight = null;
			});
		}
		const resp = await complianceInFlight;
		if (resp?.response?.status === 200) {
			const { data = [] } = resp?.apiPayload ?? {};
			const result = data?.[0];
			updateBusinessRegistrationData(result);
			setSessionDetails(result ?? []);
		}

		/* A 400 HERE IS NOT A FAULT — it is how clients.api says "this business has
		   no compliance record yet" (it throws BadRequest('No data found') on an
		   empty list, upstream master included), and this branch is the panel
		   ACTING on that: show the verification view. The toast is suppressed in
		   hooks/network/network.ts for the same reason. Left as it is rather than
		   "fixed" in the service, because every other consumer of that endpoint in
		   production reads the same 400. */
		if (resp?.errorCode === 400) {
			setBusinessRegistration((prev: IBusinessRegistrationState) => ({
				...prev,
				isViewShow: true,
			}));
		}
	};

	const getComplianceSessionStatus = async (activeIdx?: string, reInitiateState?: boolean) => {
		let url = `${API_URL.BUSINESS_COMPLIANCE}?check=true`;
		if (activeIdx) {
			url = `${API_URL.BUSINESS_COMPLIANCE}?check=true&accountType=${activeIdx}&reInitiate=${reInitiateState ?? false}`;
		}
		const resp = await get(url);
		if (resp?.response?.status === 200) {
			const { data = [] } = resp?.apiPayload ?? {};
			const result = data?.[0];
			setSessionDetails(result ?? []);
			return result;
		}
	};

	const fetchSessionDeatil = async (sessionId: string) => {
		setIsSessionLoading(true);
		const resp = await getSessionData(
			`${API_URL.COMPLEX_SESSION_DETAILS}/${sessionId}?include=true`
		);

		if (resp.message === 'ok') {
			const { kyb = [], kyc = [] } = resp.data ?? {};

			const kybData = structuredClone(kyb[0]?.data?.[0]);
			const kycData = structuredClone(kyc[0]?.data);
			if (kycData) {
				kycData['success'] = true;
				setBusinessRegistration((prev: IBusinessRegistrationState) => ({
					...prev,
					kyc: kycData,
				}));
			}
			if (kybData) {
				kybData['success'] = true;
				setBusinessRegistration((prev: IBusinessRegistrationState) => ({
					...prev,
					kyb: kybData,
				}));
			}
		}
		setIsSessionLoading(false);
	};

	return {
		getComplianceData,
		getComplianceSessionStatus,
		updateBusinessRegistrationData,
		fetchSessionDeatil,
		isSessionLoading,
	};
};
